import type { ContextBlock, PipelineStage, StageResult } from '../types';
import { draftText } from '../draft-text';
import { isEmbedderReady, embedText, cosineSimilarity } from '../../nlp/embeddings';
import { allChunkRefs } from '../../nlp/chunk-index';
import { getCachedEmbedding } from '../../db/embeddings-repo';

export const a3EmbeddingRanking: PipelineStage = {
  id: 'A3',
  group: 'A',
  label: 'Embedding-based chunk ranking',
  description:
    'Rank project chunks by local embedding similarity to your question; include only the top-k chunks that fit the context budget.',
  impact: 'High',
  defaultEnabled: true,
  implemented: true,
  params: {
    budgetTokens: { type: 'number', default: 4000, min: 500, max: 20000, label: 'Context token budget' },
    topK: { type: 'number', default: 8, min: 1, max: 50, label: 'Top-K chunks' },
  },

  async run(draft, params, ctx): Promise<StageResult> {
    const tokensBefore = ctx.estimateTokens(draftText(draft));

    // Quality guardrail: never await a cold model load inside the 2s stage
    // budget. warmEmbedder() is called eagerly at app start (see main.ts);
    // if it isn't ready yet, skip cleanly rather than block.
    if (!isEmbedderReady()) {
      return noOp(draft, tokensBefore, ['Embedding model not loaded yet — skipped.']);
    }

    const openPaths = new Set(draft.openFiles.map((f) => f.path));
    // Files already open get full/extracted treatment from A1 — A3's value is
    // surfacing relevant content from files the user hasn't opened.
    const candidates = allChunkRefs().filter((c) => !openPaths.has(c.path));
    if (candidates.length === 0) {
      return noOp(draft, tokensBefore, ['No indexed chunks outside the currently open files.']);
    }

    let queryVector: Float32Array;
    try {
      queryVector = await embedText(draft.userMessage);
    } catch (err) {
      return {
        draft,
        report: { stageId: 'A3', tokensBefore, tokensAfter: tokensBefore, tokensSaved: 0, notes: ['Failed to embed the query — sent without ranked chunks.'] },
        warning: err instanceof Error ? err.message : String(err),
      };
    }

    const scored: Array<{ path: string; text: string; startLine: number; endLine: number; score: number }> = [];
    for (const chunk of candidates) {
      const cached = await getCachedEmbedding(chunk.contentHash);
      if (!cached) continue; // background indexing hasn't embedded this one yet
      scored.push({ ...chunk, score: cosineSimilarity(queryVector, cached.vector) });
    }
    scored.sort((a, b) => b.score - a.score);

    const budgetTokens = Number(params.budgetTokens) || 4000;
    const topK = Number(params.topK) || 8;
    const picked: typeof scored = [];
    let used = 0;
    for (const s of scored) {
      if (picked.length >= topK) break;
      const t = ctx.estimateTokens(s.text);
      if (used + t > budgetTokens) continue;
      picked.push(s);
      used += t;
    }

    if (picked.length === 0) {
      return noOp(draft, tokensBefore, ['No cached embeddings available yet for out-of-context files (background indexing still catching up).']);
    }

    const block: ContextBlock = {
      id: 'a3:ranked-chunks',
      label: `Ranked relevant chunks (top ${picked.length})`,
      // Ranking is query-dependent — it changes every message, so this block
      // is explicitly NOT cacheable (see D2, which relies on this flag).
      content: picked.map((p) => `# ${p.path}:${p.startLine + 1}-${p.endLine + 1}\n${p.text}`).join('\n\n'),
      cacheable: false,
    };
    const newDraft = { ...draft, contextBlocks: [...draft.contextBlocks, block] };
    const tokensAfter = ctx.estimateTokens(draftText(newDraft));

    return {
      draft: newDraft,
      report: {
        stageId: 'A3',
        tokensBefore,
        tokensAfter,
        tokensSaved: Math.max(0, tokensBefore - tokensAfter),
        notes: [`Included ${picked.length} of ${scored.length} embedded chunk(s), ranked by similarity to your question, within a ${budgetTokens}-token budget.`],
      },
    };
  },
};

function noOp(draft: Parameters<PipelineStage['run']>[0], tokensBefore: number, notes: string[]): StageResult {
  return { draft, report: { stageId: 'A3', tokensBefore, tokensAfter: tokensBefore, tokensSaved: 0, notes } };
}
